//= require personalised-config.js
// CUSTOM: personalised-bundle-state.js

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};

  /**
   * Private utility to validate bundle data structure and contents
   * @private
   * @param {Object} bundleData - The bundle data to validate
   * @returns {boolean} True if valid, false otherwise
   */
  const _validateBundleData = (bundleData) => {
    // Check basic structure
    if (!bundleData?.subscription?.order_interval_frequency || 
        !Array.isArray(bundleData.items)) {
      if (window.personalised?.debugMode) console.debug('[_validateBundleData] Invalid bundle structure', { 
        hasSubscription: !!bundleData?.subscription,
        hasFrequency: !!bundleData?.subscription?.order_interval_frequency,
        hasItems: Array.isArray(bundleData?.items)
      });
      return false;
    }

    // Validate frequency
    const frequency = String(bundleData.subscription.order_interval_frequency);
    if (!['30', '60', '90'].includes(frequency)) {
      if (window.personalised?.debugMode) console.debug('[_validateBundleData] Invalid frequency:', frequency);
      return false;
    }

    // Validate each item has valid product and variant IDs
    return bundleData.items.every(item => {
      const validProductId = window.personalised.utils.normalizeShopifyId(item.productId) !== null;
      const validVariantId = window.personalised.utils.normalizeShopifyId(item.variantId) !== null;
      
      const isValid = item.productId && 
                      item.variantId && 
                      validProductId && 
                      validVariantId;
                      
      if (!isValid) {
        if (window.personalised?.debugMode) console.debug('[_validateBundleData] Invalid item detected:', {
          item,
          validProductId,
          validVariantId
        });
      }
      
      return isValid;
    });
  };

  /**
   * Internal utility to validate and normalize a selling plan ID for a variant
   * @private
   */
  const isValidSellingPlanId = (planId, variantId) => {
    if (!planId || (typeof planId !== 'string' && typeof planId !== 'number')) {
      return false;
    }
    
    const numericPlanId = Number(planId);
    if (isNaN(numericPlanId)) return false;
    
    const normalizedVariantId = window.personalised.utils.normalizeShopifyId(variantId);
    const variantPlans = window.personalised.sellingPlans?.[normalizedVariantId];
    
    return variantPlans ? Object.values(variantPlans).some(id => Number(id) === numericPlanId) : true;
  };
  
  /**
   * Private utility to normalize incoming item data
   * @private
   * @param {Object} rawItem - Raw item data to normalize
   * @returns {Object|null} Normalized item object or null if invalid
   */
  const normalizeIncomingItem = (rawItem) => {
    if (!rawItem) {
      if (window.personalised?.debugMode) console.debug('[normalizeIncomingItem] Received null or undefined item');
      return null;
    }
    
    if (!rawItem.productId || !rawItem.variantId) {
      if (window.personalised?.debugMode) console.debug('[normalizeIncomingItem] Item missing required IDs', rawItem);
      return null;
    }
    
    const normalizedProductId = window.personalised.utils.normalizeShopifyId(rawItem.productId);
    const normalizedVariantId = window.personalised.utils.normalizeShopifyId(rawItem.variantId);
    
    if (normalizedProductId === null || normalizedVariantId === null) {
      if (window.personalised?.debugMode) console.debug('[normalizeIncomingItem] Failed to normalize IDs', {
        originalProductId: rawItem.productId,
        originalVariantId: rawItem.variantId,
        normalizedProductId,
        normalizedVariantId
      });
      return null;
    }
    
    // Check for valid selling plan ID if provided
    const hasValidSellingPlan = rawItem.sellingPlanId ? 
      isValidSellingPlanId(rawItem.sellingPlanId, normalizedVariantId) : 
      true;
    
    return {
      productId: normalizedProductId,
      variantId: normalizedVariantId,
      collectionId: rawItem.collectionId,
      quantity: typeof rawItem.quantity === 'number' ? rawItem.quantity : 1,
      sellingPlanId: hasValidSellingPlan ? rawItem.sellingPlanId : null
    };
  };
  
  // Bundle state implementation using closure pattern
  const bundle = (function() {
    // Private internal state
    const bundleState = {
      items: [],
      frequency: '60',
      multiplier: 2,
      meta: {} // Object to store metadata
    };
    
    /**
     * Load state from storage and emit events
     */
    function load() {
      const stored = window.personalised.storageManager.getBundleState();
      if (!stored) return;
      
      bundleState.frequency = String(stored.frequency || '60');
      bundleState.multiplier = window.personalised.config.getFrequencyMultiplier(bundleState.frequency);
      
      // Ensure items is an array and filter out invalid entries
      const normalizedItems = Array.isArray(stored.items) 
        ? stored.items.map(normalizeIncomingItem).filter(Boolean) 
        : [];
      
      bundleState.items = normalizedItems;
      
      // Additional normalization pass for variant IDs to ensure consistency
      if (bundleState.items.length > 0) {
        bundleState.items.forEach(item => {
          // Ensure variant IDs are consistently normalized using our utility
          item.variantId = window.personalised.utils.normalizeShopifyId(item.variantId);
          // Ensure product IDs are also normalized
          item.productId = window.personalised.utils.normalizeShopifyId(item.productId);
        });
      }
      
      // Log validation summary
      if (window.personalised?.debugMode) {
        console.debug('[bundle.load] Loaded bundle state:', {
          frequency: bundleState.frequency,
          multiplier: bundleState.multiplier,
          originalItemCount: Array.isArray(stored.items) ? stored.items.length : 0,
          validItemCount: bundleState.items.length,
          invalidItems: Array.isArray(stored.items) ? stored.items.length - bundleState.items.length : 0
        });
      }
      
      // Load metadata from storageManager
      loadMetadata();
      
      window.personalised.eventBus.emit('bundleStateLoaded', {
        frequency: bundleState.frequency,
        itemCount: bundleState.items.length,
        hasMeta: Object.keys(bundleState.meta).length > 0
      });
    }
    
    /**
     * Loads metadata from storageManager into bundleState.meta
     * @private
     */
    function loadMetadata() {
      // Get all metadata from storage
      const allMetadata = window.personalised.storageManager.getBundleMetadata() || {};
      
      // List of critical metadata keys we want to track
      const criticalKeys = [
        'bundleSelectionId',
        'subscriptionId',
        'purchaseItemId',
        'originalFrequency',
        'externalProductId',
        'externalVariantId',
        'bundleRechargeId'
      ];
      
      // Initialize empty metadata object
      bundleState.meta = {};
      
      // Copy only critical keys to bundleState.meta
      criticalKeys.forEach(key => {
        if (allMetadata.hasOwnProperty(key) && allMetadata[key] !== null) {
          bundleState.meta[key] = allMetadata[key];
        }
      });
      
      // Log metadata for debugging
      if (window.personalised?.debugMode) console.debug('[loadMetadata] Loaded metadata:', { 
        count: Object.keys(bundleState.meta).length,
        keys: Object.keys(bundleState.meta)
      });
    }
    
    /**
     * Save current state to storage and emit event
     */
    /**
     * Saves current state and metadata to storage
     */
    function save() {
      // Save bundle state
      window.personalised.storageManager.saveBundleState({
        frequency: bundleState.frequency,
        multiplier: bundleState.multiplier,
        items: bundleState.items
      });
      
      // Save all metadata
      saveMetadata();
      
      window.personalised.eventBus.emit('bundleStateSaved', {
        frequency: bundleState.frequency,
        itemCount: bundleState.items.length,
        hasMeta: Object.keys(bundleState.meta).length > 0
      });
    }
    
    /**
     * Saves metadata from bundleState.meta to storageManager
     * @private
     */
    function saveMetadata() {
      if (!bundleState.meta || typeof bundleState.meta !== 'object') {
        return;
      }
      
      // Save each key individually to ensure proper validation
      Object.entries(bundleState.meta).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          window.personalised.storageManager.setMetadata(key, value);
        }
      });
      
      if (window.personalised?.debugMode) console.debug('[saveMetadata] Saved metadata keys:', Object.keys(bundleState.meta));
    }

    /**
     * Loads bundle state from Recharge bundle selection data
     * @param {Object} bundleData - The bundle data from Recharge API
     */
    function loadFromRecharge(bundleData) {
      try {
        // Use the _validateBundleData utility to validate bundle structure
        if (!_validateBundleData(bundleData)) {
          if (window.personalised?.debugMode) console.warn('[loadFromRecharge] Bundle data validation failed');
          throw new Error('Invalid bundle data structure or contents');
        }

        // Extract frequency from subscription (already validated)
        const frequency = bundleData.subscription.order_interval_frequency;

        // Update frequency and multiplier
        bundleState.frequency = String(frequency);
        bundleState.multiplier = window.personalised.config.getFrequencyMultiplier(frequency);

        // Process and normalize items
        const normalizedItems = [];
        bundleData.items.forEach(item => {
          if (!item.productId || !item.variantId) {
            if (window.personalised?.debugMode) console.warn('Skipping invalid bundle item:', item);
            return;
          }
          
          // Normalize IDs using utilities
          const normalizedProductId = window.personalised.utils.normalizeShopifyId(item.productId);
          const normalizedVariantId = window.personalised.utils.normalizeShopifyId(item.variantId);
          
          if (window.personalised?.debugMode) console.debug('[loadFromRecharge] Normalizing IDs for bundle item', {
            originalProductId: item.productId,
            normalizedProductId,
            originalVariantId: item.variantId,
            normalizedVariantId
          });
          
          // Skip invalid IDs
          if (normalizedProductId === null || normalizedVariantId === null) {
            if (window.personalised?.debugMode) console.warn('Skipping item with invalid IDs:', item);
            return;
          }

          normalizedItems.push({
            productId: normalizedProductId,
            variantId: normalizedVariantId,
            collectionId: item.collectionId || null,
            quantity: 1, // Reset to base quantity 1 – quantities from Recharge include frequency multiplier
            sellingPlanId: item.sellingPlanId || null
          });
        });
        
        // Log the final items array for debugging
        if (window.personalised?.debugMode) console.log('[loadFromRecharge] Final normalized items array:', normalizedItems);
        
        if (normalizedItems.length === 0) {
          if (window.personalised?.debugMode) console.warn('[loadFromRecharge] No valid items found in bundle data');
        }
        
        // Assign items directly — do NOT call setItems() here (see PR-6.1 comment below)
        bundleState.items = normalizedItems;

        // Store bundle metadata for update operations
        if (bundleData.bundle && bundleData.subscription) {
          // Log raw objects for diagnostic purposes
          if (window.personalised?.debugMode) console.log('🔍 [DIAGNOSTIC] loadFromRecharge - Raw Objects:', {
            bundle: bundleData.bundle,
            subscription: bundleData.subscription
          });
          
          // Extract and validate critical IDs
          const bundleRechargeId = bundleData.bundle.bundleRechargeId || bundleData.bundle.id;
          const purchaseItemId = bundleData.bundle.purchaseItemId;
          
          if (window.personalised?.debugMode) console.log('🔍 [DIAGNOSTIC] loadFromRecharge - Critical IDs:', {
            bundleRechargeId,
            purchaseItemId
          });
          
          // Validate both IDs are present
          if (!bundleRechargeId || !purchaseItemId) {
            console.error('❌ [ERROR] Missing bundle_id or purchase_item_id from Recharge response', {
              hasBundleRechargeId: !!bundleRechargeId,
              hasPurchaseItemId: !!purchaseItemId
            });
            throw new Error('Missing required bundle identifiers from Recharge');
          }
          
          // Update internal metadata object
          bundleState.meta = {
            bundleSelectionId: bundleData.bundle.id,
            subscriptionId: bundleData.subscription.id,
            purchaseItemId: purchaseItemId,
            originalFrequency: bundleData.subscription.order_interval_frequency,
            externalProductId: bundleData.bundle.external_product_id,
            externalVariantId: bundleData.bundle.external_variant_id,
            bundleRechargeId: bundleRechargeId
          };
          
          // Store critical IDs in the metadata storage
          window.personalised.storageManager.setMetadata('bundleSelectionId', bundleData.bundle.id);
          window.personalised.storageManager.setMetadata('subscriptionId', bundleData.subscription.id);
          window.personalised.storageManager.setMetadata('purchaseItemId', purchaseItemId);
          window.personalised.storageManager.setMetadata('originalFrequency', bundleData.subscription.order_interval_frequency);
          
          // Optional metadata that might be useful
          window.personalised.storageManager.setMetadata('externalProductId', bundleData.bundle.external_product_id);
          window.personalised.storageManager.setMetadata('externalVariantId', bundleData.bundle.external_variant_id);
          window.personalised.storageManager.setMetadata('bundleRechargeId', bundleRechargeId);
          
          if (window.personalised?.debugMode) console.log('✅ [SUCCESS] Bundle IDs loaded correctly and stored in metadata', {
            bundleSelectionId: bundleData.bundle.id,
            purchaseItemId: purchaseItemId,
            subscriptionId: bundleData.subscription.id
          });
        }
        
        // PR-6.1: items were already assigned to bundleState.items directly above.
        // Do NOT use bundleAPI.setItems() here — setItems() unconditionally calls
        // markBundleModified(), which is wrong for a system-initiated Recharge load.
        // save() persists the state correctly without triggering the modified flag.
        save();
        
        // Emit event after ensuring items are set
        window.personalised.eventBus.emit('bundleLoadedFromRecharge', { 
          frequency: bundleState.frequency, 
          itemCount: bundleState.items.length 
        });
        
        // Trigger UI update through render hook if available
        if (window.personalised.renderHooks?.renderBundleItems) {
          window.personalised.renderHooks.renderBundleItems();
        }
        
        return true;

      } catch (err) {
        console.error('Failed to load bundle from Recharge:', err);
        throw err; // Re-throw to allow caller to handle
      }
    }

    /**
     * Initializes bundle state for edit mode with subscription data from Recharge
     * @param {string|number} subscriptionId - The subscription ID to load
     * @returns {Promise<boolean>} True if initialization succeeded, false otherwise
     */
    let _initializationInProgress = false;
    
    async function initializeEditMode(subscriptionId) {
      // Guard against parallel initialization
      if (_initializationInProgress) {
        console.warn('Bundle initialization already in progress');
        return false;
      }
      
      // Check if local bundle state has been modified in this session
      const stateModified = window.personalised.storageManager.isBundleModified();
      
      if (stateModified) {
        console.info('[Edit Mode] Local bundle state detected – skipping Recharge fetch');
        _initializationInProgress = false;
        return load();
      }
      
      _initializationInProgress = true;
      
      try {
        if (window.personalised?.config?.debugMode) {
          console.debug('[initializeEditMode] Starting with subscription ID:', subscriptionId, {
            currentMode: window.personalised.utils.getMode(),
            stateModified: stateModified
          });
        }
        
        if (!subscriptionId) {
          console.error('[initializeEditMode] No subscription ID provided');
          _initializationInProgress = false;
          return false;
        }
        
        // Get bundle data from Recharge using utility
        const bundleData = await window.personalised.rechargeUtils.getBundleSelectionForSubscription(subscriptionId);
        
        console.debug('[initializeEditMode] Bundle data received:', {
          success: !!bundleData,
          hasItems: bundleData && Array.isArray(bundleData.items) && bundleData.items.length > 0,
          itemCount: bundleData?.items?.length || 0
        });
        
        if (!bundleData) {
          console.warn('[initializeEditMode] No bundle data received for subscription ID:', subscriptionId);
          _initializationInProgress = false;
          return false;
        }
        
        // Validate bundle data before attempting to load it
        if (!_validateBundleData(bundleData)) {
          console.warn('[initializeEditMode] Bundle data validation failed for subscription ID:', subscriptionId);
          _initializationInProgress = false;
          return false;
        }
        
        // Load from Recharge data
        loadFromRecharge(bundleData);
        
        // Log items after loading to confirm proper loading
        console.log('[initializeEditMode] Items after loading from Recharge:', 
          bundleState.items.map(item => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity
          }))
        );
        
        // Double check that items are correctly normalized
        if (bundleState.items.length === 0) {
          console.warn('[initializeEditMode] No items found after loading from Recharge');
        }
        
        console.log('[initializeEditMode] Successfully loaded bundle state from Recharge');
        
        // Set flag to indicate we loaded from Recharge so cart sync knows to be careful
        window.personalised.storageManager.setMetadata('loadedFromRecharge', true);
        bundleState.meta.loadedFromRecharge = true;
        console.debug('[initializeEditMode] Setting loadedFromRecharge flag in metadata to prevent unintended cart sync');
        
        // Ensure metadata is loaded into bundleState.meta
        loadMetadata();
        
        // Log the final state of metadata
        console.debug('[initializeEditMode] Final metadata state:', {
          inMemory: Object.keys(bundleState.meta).length,
          inStorage: Object.keys(window.personalised.storageManager.getBundleMetadata() || {}).length
        });
        
        _initializationInProgress = false;
        return true;
      } catch (err) {
        console.error('[initializeEditMode] Failed to initialize edit mode:', err);
        
        // Fallback to loading any existing local state as a last resort
        load();
        
        _initializationInProgress = false;
        return false;
      }
    }
    
    // Public API
    return {
      // State loading and access
      load,
      save,
      getItems: () => [...bundleState.items],
      getItemCount: () => bundleState.items.length,
      hasItems: () => bundleState.items.length > 0,
      
      // Metadata access methods
      getMetadata: (key, defaultValue = null) => {
        const value = bundleState.meta && bundleState.meta.hasOwnProperty(key) ? bundleState.meta[key] : defaultValue;
        
        if (value === null || value === undefined) {
          // Define critical keys by mode
          const criticalKeysByMode = {
            guest: [],
            new: [],
            edit: ['bundleSelectionId', 'subscriptionId', 'purchaseItemId']
          };
          
          // Only log warning if key is critical for current mode
          const mode = window.personalised.utils.getMode();
          if (mode === 'edit' && criticalKeysByMode.edit.includes(key)) {
            console.warn(`[Bundle] Missing critical metadata key: ${key}`);
          }
        }
        
        return value;
      },
      
      getAllMetadata: () => {
        // Log warning if no metadata is present
        if (!bundleState.meta || Object.keys(bundleState.meta).length === 0) {
          console.warn('[Bundle] No metadata present in bundle state');
        }
        return { ...bundleState.meta };
      },
      
      setMetadata: (key, value) => {
        if (!key || typeof key !== 'string') {
          console.warn('[bundle.setMetadata] Invalid key provided');
          return false;
        }
        
        // Update internal state
        if (!bundleState.meta) bundleState.meta = {};
        bundleState.meta[key] = value;
        
        // Persist to storage
        window.personalised.storageManager.setMetadata(key, value);
        return true;
      },
      
      clearMetadata: (key) => {
        if (!bundleState.meta || !key) return false;
        
        if (bundleState.meta.hasOwnProperty(key)) {
          delete bundleState.meta[key];
          window.personalised.storageManager.clearMetadata(key);
          return true;
        }
        
        return false;
      },
      
      hasMetadata: (key) => {
        return bundleState.meta && bundleState.meta.hasOwnProperty(key) && bundleState.meta[key] !== null;
      },
      
      // Add setItems method to update all items at once
      setItems(newItems) {
        if (!Array.isArray(newItems)) {
          console.error('[setItems] Invalid items array provided:', newItems);
          return false;
        }
        
        // Use normalizeIncomingItem for consistent item normalization
        const normalizedItems = [];
        let skippedCount = 0;
        
        // Process each item through the normalizer
        newItems.forEach(item => {
          const normalizedItem = normalizeIncomingItem(item);
          
          // Only include items that pass normalization
          if (normalizedItem) {
            normalizedItems.push(normalizedItem);
          } else {
            skippedCount++;
          }
        });
        
        // Log debug summary
        console.log('[setItems] Normalized items:', {
          total: newItems.length,
          accepted: normalizedItems.length,
          skipped: skippedCount
        });
        
        bundleState.items = normalizedItems;
        save();
        
        // Mark bundle as modified to prevent Recharge reload from overwriting changes
        window.personalised.storageManager.markBundleModified();
        console.debug('Bundle modified: [setItems]');
        
        // Trigger UI update through render hook if available
        if (window.personalised.renderHooks?.renderBundleItems) {
          window.personalised.renderHooks.renderBundleItems();
        }
        
        return true;
      },
      
      // Item operations
      addItem({ productId, variantId, collectionId, quantity = 1, sellingPlanId }) {
        // Input validation - check for required fields
        if (!productId || !variantId) {
          console.warn('[addItem] Missing required fields:', { 
            hasProductId: !!productId, 
            hasVariantId: !!variantId 
          });
          return false;
        }
        
        // Normalize IDs first for further processing
        const normalizedProductId = window.personalised.utils.normalizeShopifyId(productId);
        const normalizedVariantId = window.personalised.utils.normalizeShopifyId(variantId);
        
        // Skip if any IDs are invalid
        if (normalizedProductId === null || normalizedVariantId === null) {
          console.warn('[addItem] Invalid product or variant ID, skipping add', {
            productId,
            variantId,
            normalizedProductId,
            normalizedVariantId
          });
          return false;
        }
        
        // Determine default selling plan for this variant
        const defaultPlanLabel = window.personalised.config?.subscription?.DEFAULT_SELLING_PLAN_LABELS?.[bundleState.frequency] || '30_day_subscription';
        const defaultPlanId = window.personalised.sellingPlans?.[normalizedVariantId]?.[defaultPlanLabel];
        
        // Validate the provided selling plan ID, fall back to default if invalid
        const validatedSellingPlanId = isValidSellingPlanId(sellingPlanId, normalizedVariantId) 
          ? sellingPlanId 
          : defaultPlanId;
        
        if (window.personalised?.config?.debugMode) {
          console.debug('[addItem] Selling plan selection:', {
            providedPlanId: sellingPlanId,
            isValid: isValidSellingPlanId(sellingPlanId, normalizedVariantId),
            defaultPlanId,
            finalPlanId: validatedSellingPlanId
          });
        }
        
        // Now normalize the full item with the validated selling plan
        const normalizedItem = normalizeIncomingItem({ 
          productId: normalizedProductId, 
          variantId: normalizedVariantId, 
          collectionId, 
          quantity, 
          sellingPlanId: validatedSellingPlanId
        });
        
        if (!normalizedItem) {
          console.warn('[addItem] Item normalization failed');
          return false;
        }
        
        // Remove any existing variant of the same product
        this.removeItemByVariantId(variantId);
        
        bundleState.items.push(normalizedItem);
        save();
        
        // Mark bundle as modified to prevent Recharge reload from overwriting changes
        window.personalised.storageManager.markBundleModified();
        console.debug('Bundle modified: [addItem]');
        
        // Trigger UI update through render hook if available
        if (window.personalised.renderHooks?.renderBundleItems) {
          window.personalised.renderHooks.renderBundleItems();
        }
        
        return true;
      },
      
      removeItemByVariantId(variantId) {
        try {
          // Validate input
          if (!variantId) {
            console.warn('[removeItemByVariantId] No variant ID provided');
            return false;
          }
          
          // Use utility for ID normalization and validation
          const normalizedId = window.personalised.utils.normalizeShopifyId(variantId);
          if (normalizedId === null) {
            console.warn('[removeItemByVariantId] Invalid variant ID format:', variantId);
            return false;
          }
          
          const idx = bundleState.items.findIndex(item => 
            window.personalised.utils.shopifyIdsEqual(item.variantId, normalizedId)
          );
          
          if (idx === -1) {
            // Item not found - not necessarily an error
            return false;
          }
          
          bundleState.items.splice(idx, 1);
          save();
          
          // Mark bundle as modified to prevent Recharge reload from overwriting changes
          window.personalised.storageManager.markBundleModified();
          console.debug('Bundle modified: [removeItemByVariantId]');
          
          // Trigger UI update through render hook if available
          if (window.personalised.renderHooks?.renderBundleItems) {
            window.personalised.renderHooks.renderBundleItems();
          }
          
          return true;
        } catch (err) {
          console.warn('[removeItemByVariantId] Error removing item:', err);
          return false;
        }
      },
      
      findItem(variantId) {
        const normalizedId = window.personalised.utils.normalizeShopifyId(variantId);
        return bundleState.items.find(item => 
          window.personalised.utils.shopifyIdsEqual(item.variantId, normalizedId)
        );
      },
      
      // Frequency management
      setFrequency(freq) {
        try {
          // Convert to string for validation
          const normalizedFreq = String(freq);
          
          // Use validation module to check frequency value
          const isValid = ['30', '60', '90'].includes(normalizedFreq);
          if (!isValid) {
            console.warn(`[setFrequency] Invalid frequency value: ${freq}. Must be one of: 30, 60, 90`);
            return false;
          }
          
          bundleState.frequency = normalizedFreq;
          bundleState.multiplier = window.personalised.config.getFrequencyMultiplier(bundleState.frequency);
          
          // Update selling plan IDs for all items to match new frequency
          if (bundleState.items && bundleState.items.length > 0) {
            // Get the selling plan label for the new frequency from config
            const freqSellingPlanLabel = window.personalised.config?.subscription?.DEFAULT_SELLING_PLAN_LABELS?.[normalizedFreq];
            
            // Update each item's selling plan ID
            bundleState.items.forEach(item => {
              if (item.variantId && window.personalised.sellingPlans?.[item.variantId]) {
                const newSellingPlanId = window.personalised.sellingPlans[item.variantId][freqSellingPlanLabel];
                if (newSellingPlanId) {
                  item.sellingPlanId = newSellingPlanId;
                  if (window.personalised?.debugMode) {
                    console.debug(`[setFrequency] Updated selling plan ID for variant ${item.variantId} to ${newSellingPlanId} (${freqSellingPlanLabel})`);
                  }
                }
              }
            });
          }
          
          save();
          
          // Mark bundle as modified to prevent Recharge reload from overwriting changes
          window.personalised.storageManager.markBundleModified();
          console.debug('Bundle modified: [setFrequency]');
          
          // Trigger UI update through render hook if available
          if (window.personalised.renderHooks?.renderBundleItems) {
            window.personalised.renderHooks.renderBundleItems();
          }
          
          return true;
        } catch (err) {
          console.warn('[setFrequency] Error setting frequency:', err);
          return false;
        }
      },
      
      getFrequency: () => bundleState.frequency,
      
      // Frequency multiplier methods
      getFrequencyMultiplier: () => bundleState.multiplier || 1,
      setFrequencyMultiplier: (value) => {
        try {
          // Use the validation module instead of local validation
          const isValid = window.personalised.validation.isValidFrequencyMultiplier(value);
          if (!isValid) {
            console.warn('[setFrequencyMultiplier] Invalid multiplier value:', value);
            return false;
          }
          
          bundleState.multiplier = value;
          save();
          
          // Mark bundle as modified
          window.personalised.storageManager.markBundleModified();
          console.debug('Bundle modified: [setFrequencyMultiplier]');
          
          // Trigger UI update through render hook
          if (window.personalised.renderHooks?.renderBundleItems) {
            window.personalised.renderHooks.renderBundleItems();
          }
          
          return true;
        } catch (err) {
          console.warn('[setFrequencyMultiplier] Error setting multiplier:', err);
          return false;
        }
      },
      
      // Bundle checks
      isInBundle(variantId) {
        const normalizedId = window.personalised.utils.normalizeShopifyId(variantId);
        return bundleState.items.some(item => 
          window.personalised.utils.shopifyIdsEqual(item.variantId, normalizedId)
        );
      },
      
      isProductInBundle(productId) {
        const normalizedId = window.personalised.utils.normalizeShopifyId(productId);
        return bundleState.items.some(item => 
          window.personalised.utils.shopifyIdsEqual(item.productId, normalizedId)
        );
      },
      
      // Bundle data formatting
      toRechargeFormat() {

        // Get Recharge property keys from config
        const BUNDLE_PARENT_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.BUNDLE_PARENT_PROPERTY || '_rc_bundle_parent';
        const BUNDLE_PARENT_KEY = window.personalised.config?.bundle?.BUNDLE_PARENT_PROPERTY_KEY || 'personalised-pack';
        const BUNDLE_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.BUNDLE_PROPERTY || '_rc_bundle';
        const BUNDLE_VARIANT_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.BUNDLE_VARIANT_PROPERTY || '_rc_bundle_variant';
        const VARIANT_PARENT_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.VARIANT_PARENT_PROPERTY || '_rc_variant_parent';
        const BUNDLE_INDEX_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.BUNDLE_INDEX_PROPERTY || '_rc_bundle_index';
        
        // Get metadata values exclusively from bundleState.meta using getMetadata()
        const bundleSelectionId = this.getMetadata('bundleSelectionId');
        const subscriptionId = this.getMetadata('subscriptionId');
        const purchaseItemId = this.getMetadata('purchaseItemId');
        const externalProductId = this.getMetadata('externalProductId');
        const externalVariantId = this.getMetadata('externalVariantId');
        
        // Log available metadata for debugging
        console.debug('[toRechargeFormat] Available metadata', { 
          hasBundleSelectionId: !!bundleSelectionId,
          hasSubscriptionId: !!subscriptionId,
          hasPurchaseItemId: !!purchaseItemId,
          hasExternalProductId: !!externalProductId,
          hasExternalVariantId: !!externalVariantId,
          source: 'bundle.getMetadata()'
        });
        
        return {
          // Defines how often the subscription will be delivered
          selling_plan: {
            frequency: bundleState.frequency // Delivery interval in days (30, 60, 90)
          },
          // Recharge bundle selection identifier (required for order updates)
          bundle_selection_id: bundleSelectionId,
          // Recharge subscription identifier (required for subscription updates)
          subscription_id: subscriptionId,
          // Recharge purchase item identifier (required for checkout)
          purchase_item_id: purchaseItemId,
          // External product reference for third-party integrations
          external_product_id: externalProductId,
          // External variant reference for third-party integrations
          external_variant_id: externalVariantId,
          // Array of bundle items that will be processed by Recharge
          items: bundleState.items.map((item, index) => {
            // Log warnings for missing required values
            if (!item.productId) {
              console.warn('Missing productId in bundle item:', item);
            }
            if (!item.variantId) {
              console.warn('Missing variantId in bundle item:', item);
            }
            
            // Convert internal numeric IDs to strings for API using formatShopifyIdForApi
            const apiProductId = window.personalised.utils?.formatShopifyIdForApi 
              ? window.personalised.utils.formatShopifyIdForApi(item.productId)
              : String(item.productId);
              
            const apiVariantId = window.personalised.utils?.formatShopifyIdForApi 
              ? window.personalised.utils.formatShopifyIdForApi(item.variantId)
              : String(item.variantId);
            
            console.debug('[toRechargeFormat] formatting IDs for API', {
              internalProductId: item.productId, 
              apiProductId,
              internalVariantId: item.variantId,
              apiVariantId
            });
            
            return {
              // Shopify product ID (must be formatted for API)
              product_id: apiProductId,
              // Shopify variant ID (must be formatted for API)
              variant_id: apiVariantId,
              // Optional collection ID for categorizing bundle items
              collection_id: item.collectionId != null ? String(item.collectionId) : null,
                  // Item quantity adjusted by frequency multiplier
              // NOTE: This is where frequency multiplier is applied - do not apply again in updateBundle()
              quantity: item.quantity * bundleState.multiplier,
              // Associated selling plan ID for subscription discounts
              selling_plan_id: item.sellingPlanId,
              // Line item properties required by Recharge for bundle processing
              properties: {
                [BUNDLE_PARENT_PROPERTY]: BUNDLE_PARENT_KEY,
                [BUNDLE_PROPERTY]: item.collectionId != null ? String(item.collectionId) : null,
                [BUNDLE_VARIANT_PROPERTY]: true,
                [VARIANT_PARENT_PROPERTY]: apiProductId,
                [BUNDLE_INDEX_PROPERTY]: index + 1
              }
            };
          })
        };
      },
      
      // State reset
      reset() {
        try {
          // Check if already in default state - avoid unnecessary operations
          const isDefault = bundleState.items.length === 0 && 
                          bundleState.frequency === '60' && 
                          bundleState.multiplier === 2 && 
                          Object.keys(bundleState.meta).length === 0;
          
          if (isDefault) {
            console.debug('[reset] Bundle already in default state');
            return true;
          }
          
          // Reset to default state
          bundleState.items = [];
          bundleState.frequency = '60';
          bundleState.multiplier = 2;
          bundleState.meta = {}; // Clear metadata from memory
          
          // Clear storage
          window.personalised.storageManager.clearBundleState();
          
          // Emit reset event
          window.personalised.eventBus.emit('bundleStateReset', {});
          
          // Trigger UI update through render hook if available
          if (window.personalised.renderHooks?.renderBundleItems) {
            window.personalised.renderHooks.renderBundleItems();
          }
          
          console.debug('Bundle modified: [reset]');
          return true;
        } catch (err) {
          console.warn('[reset] Error resetting bundle state:', err);
          return false;
        }
      },
      
      /**
       * Tears down the bundle state completely, including all metadata
       * Clears in-memory state, storage, and all related metadata
       * @returns {boolean} True if teardown was successful, false otherwise
       */
      teardown() {
        try {
          // Reset in-memory state
          bundleState.items = [];
          bundleState.frequency = '60';
          bundleState.multiplier = 2;
          bundleState.meta = {}; // Clear metadata from memory
          
          // Clear storage safely
          window.personalised.storageManager.clearBundleState();
          
          // Clear all possible metadata keys
          const metadataKeys = [
            'bundleSelectionId', 'subscriptionId', 'purchaseItemId',
            'originalFrequency', 'externalProductId', 'externalVariantId',
            'bundleRechargeId', 'loadedFromRecharge', 'bundleModified'
          ];
          
          metadataKeys.forEach(key => {
            window.personalised.storageManager.clearMetadata(key);
          });
          
          // Emit event for any listeners that need to know about the teardown
          window.personalised.eventBus.emit('bundleStateTornDown', {});
          
          // Trigger UI update through render hook if available
          if (window.personalised.renderHooks?.renderBundleItems) {
            window.personalised.renderHooks.renderBundleItems();
          }
          
          console.debug('Bundle modified: [teardown] Bundle state completely cleared');
          return true;
        } catch (err) {
          console.error('Failed to clear personalised bundle state during teardown:', err);
          return false;
        }
      },
      
      // Add missing methods
      loadFromRecharge,
      initializeEditMode
    };
  })();
  
  // Expose bundle interface
  window.personalised.bundle = bundle;
  
  // Log confirmation of restored methods
  console.debug('[BundleState] Ready: initializeEditMode and loadFromRecharge exposed');
})();
